import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import NetInfo from '@react-native-community/netinfo'
import { useIsRestoring, useQuery, useQueryClient } from '@tanstack/react-query'

import { credentialQueryOptions } from '../queries/auth-queries'
import { queryKeys } from '../queries/query-keys'
import { createAccessTokenGetter } from '../utils/auth-access-token'
import {
  getAuthErrorKind,
  isIgnorableAuthSessionError,
  isInvalidSessionAuthError,
  normalizeAuthError,
} from '../utils/auth-errors'
import { getStoredIsAuthenticated } from '../utils/auth-session-storage'
import { captureException } from '../utils/sentry'
import { clearLocalSessionState } from '../utils/session-cleanup'
import { useAuth0Adapter } from './useAuth0Adapter'

const resolvedCredentialUsersThisRuntime = new Set<string>()

export function useResolvedAuthState() {
  const queryClient = useQueryClient()
  const isRestoringCache = useIsRestoring()
  const auth0 = useAuth0Adapter()
  const { user, isLoading, error: rawError, getCredentials } = auth0
  const error = normalizeAuthError(rawError)
  const [isDeviceOffline, setIsDeviceOffline] = useState<boolean | null>(null)
  const [storedAuthSession, setStoredAuthSession] = useState<{
    authUserId: string
    isAuthenticated: boolean
  } | null>(null)
  const [isRecoveringInvalidSession, setIsRecoveringInvalidSession] =
    useState(false)
  const [showInvalidSessionMessage, setShowInvalidSessionMessage] =
    useState(false)
  const handledInvalidSessionRef = useRef<string | null>(null)
  const authUserId = typeof user?.sub === 'string' ? user.sub : null
  const hasAuthUser = user != null
  const authErrorKind = getAuthErrorKind(rawError)

  const {
    data: hasValidCredentials = false,
    isPending: isCredentialsPending,
    isFetchedAfterMount: hasRefreshedCredentialsThisRuntime,
  } = useQuery({
    ...credentialQueryOptions({ authUserId, getCredentials }),
    enabled: !isLoading && hasAuthUser,
  })

  useEffect(() => {
    let isCancelled = false

    const unsubscribe = NetInfo.addEventListener((state) => {
      if (!isCancelled) {
        setIsDeviceOffline(state.isConnected === false)
      }
    })

    void NetInfo.fetch()
      .then((state) => {
        if (!isCancelled) {
          setIsDeviceOffline(state.isConnected === false)
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setIsDeviceOffline(false)
        }
      })

    return () => {
      isCancelled = true
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (authUserId == null) {
      return
    }

    let isCancelled = false

    void getStoredIsAuthenticated(authUserId)
      .then((isAuthenticated) => {
        if (!isCancelled) {
          setStoredAuthSession({ authUserId, isAuthenticated })
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setStoredAuthSession({ authUserId, isAuthenticated: false })
        }
      })

    return () => {
      isCancelled = true
    }
  }, [authUserId])

  useEffect(() => {
    if (authUserId == null || !hasRefreshedCredentialsThisRuntime) {
      return
    }

    resolvedCredentialUsersThisRuntime.add(authUserId)
  }, [authUserId, hasRefreshedCredentialsThisRuntime])

  useEffect(() => {
    if (hasAuthUser) {
      return
    }

    resolvedCredentialUsersThisRuntime.clear()
  }, [hasAuthUser])

  useEffect(() => {
    if (!isInvalidSessionAuthError(rawError)) {
      handledInvalidSessionRef.current = null
      return
    }

    const invalidSessionKey = [
      authErrorKind ?? 'unknown',
      rawError.name,
      rawError.message,
      authUserId ?? 'anonymous',
    ].join(':')

    if (handledInvalidSessionRef.current === invalidSessionKey) {
      return
    }

    handledInvalidSessionRef.current = invalidSessionKey
    setIsRecoveringInvalidSession(true)

    captureException(rawError, {
      feature: 'auth',
      tags: {
        flow: 'init',
        reason: 'missing_refresh_token',
      },
      extra: {
        authUserId: authUserId ?? 'anonymous',
      },
    })

    let isCancelled = false

    async function recoverInvalidSession() {
      try {
        await clearLocalSessionState({ authUserId, queryClient })
        await auth0.clearSession()
      } catch (sessionError) {
        if (!isIgnorableAuthSessionError(sessionError)) {
          captureException(sessionError, {
            feature: 'auth',
            tags: {
              flow: 'invalid_session_recovery',
            },
            extra: {
              authUserId: authUserId ?? 'anonymous',
            },
          })
        }
      } finally {
        if (!isCancelled) {
          setIsRecoveringInvalidSession(false)
          setShowInvalidSessionMessage(true)
        }
      }
    }

    void recoverInvalidSession()

    return () => {
      isCancelled = true
    }
  }, [auth0, authErrorKind, authUserId, queryClient, rawError])

  const authorize = useCallback(
    (options?: {
      screen_hint?: string
      prompt?: 'login' | 'none' | 'consent' | 'select_account'
      returnTo?: string
    }) => {
      return auth0.authorize(options).catch((e) => {
        if (isIgnorableAuthSessionError(e)) {
          return
        }

        console.error('Login failed', e)
        captureException(e, { feature: 'auth', tags: { flow: 'login' } })
        throw e
      })
    },
    [auth0],
  )

  const dismissInvalidSessionMessage = useCallback(() => {
    setShowInvalidSessionMessage(false)
  }, [])

  const getAccessToken = useMemo(
    () => createAccessTokenGetter({ getCredentials }),
    [getCredentials],
  )

  const hasCachedBackendUser =
    !isRestoringCache &&
    authUserId != null &&
    queryClient.getQueryData(queryKeys.user.profile(authUserId)) != null
  const hasStoredAuthenticatedSession =
    authUserId == null
      ? false
      : storedAuthSession?.authUserId === authUserId
        ? storedAuthSession.isAuthenticated
        : null
  const isOfflineTrustedSession =
    hasAuthUser &&
    hasCachedBackendUser &&
    (authUserId == null ? false : hasStoredAuthenticatedSession === true) &&
    isDeviceOffline === true &&
    !isInvalidSessionAuthError(rawError)
  const hasResolvedCredentialsFromRuntimeCache =
    authUserId != null && resolvedCredentialUsersThisRuntime.has(authUserId)
  const hasResolvedCredentialsThisRuntime =
    !hasAuthUser ||
    hasRefreshedCredentialsThisRuntime ||
    hasResolvedCredentialsFromRuntimeCache
  const isAuthReady = !isLoading || error != null
  const isAuthenticated =
    (hasAuthUser && hasValidCredentials && hasResolvedCredentialsThisRuntime) ||
    isOfflineTrustedSession
  // isDeviceOffline === null means NetInfo hasn't responded yet. We must treat
  // this as a loading state to prevent the storage scope sync from running with
  // an anonymous scope (which would call queryClient.clear() and wipe the
  // restored cache before we know the device is offline).
  const isAuthLoading =
    !isOfflineTrustedSession &&
    (isRecoveringInvalidSession ||
      !isAuthReady ||
      isDeviceOffline === null ||
      (hasAuthUser &&
        (!hasResolvedCredentialsThisRuntime || isCredentialsPending) &&
        !isLoading))

  return {
    ...auth0,
    authErrorKind,
    authUserId,
    dismissInvalidSessionMessage,
    error,
    hasAuthUser,
    hasResolvedCredentialsThisRuntime,
    hasValidCredentials,
    isAuthenticated,
    isAuthLoading,
    isRecoveringInvalidSession,
    isInvalidSessionError: showInvalidSessionMessage,
    authorize,
    getAccessToken,
  }
}
